import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, IdCard, UploadCloud } from "lucide-react";
import {
  Button,
  Card,
  FormField,
  Input,
  KycStatusBadge,
  Skeleton,
  useToast,
} from "@chotca/ui";
import {
  putToSignedUrl,
  useMyIdentity,
  usePresignIdentity,
  useSubmitIdentity,
  useWithdrawIdentity,
} from "@chotca/api-client";

const ALLOWED = new Set<"image/jpeg" | "image/png">([
  "image/jpeg",
  "image/png",
]);
const MAX_BYTES = 10 * 1024 * 1024;

type Pick = { file: File; contentType: "image/jpeg" | "image/png" } | null;

// Human-readable hints for each standardized reject code (spec §7.4). Shown to
// the user when a submission comes back REJECTED so they know how to fix it.
const REJECT_HINTS: Record<string, string> = {
  DOC_UNREADABLE: "Ảnh CCCD bị mờ hoặc thiếu góc. Vui lòng chụp lại rõ nét.",
  DOC_EXPIRED: "CCCD đã hết hạn. Vui lòng nộp giấy tờ còn hiệu lực.",
  NAME_MISMATCH: "Họ tên không khớp với CCCD. Kiểm tra lại phần khai.",
  WRONG_DOC_TYPE: "Sai loại giấy tờ. Vui lòng nộp đúng ảnh CCCD.",
  OTHER: "Hồ sơ chưa đạt. Xem chi tiết bên dưới và nộp lại.",
};

const formSchema = z.object({
  idNumber: z
    .string()
    .trim()
    .regex(/^[0-9]{9,12}$/, "Số CCCD gồm 9-12 chữ số"),
  fullName: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập họ và tên")
    .max(120, "Họ tên tối đa 120 ký tự"),
  consent: z.literal(true, { message: "Vui lòng đồng ý điều khoản" }),
});

type FormValues = z.infer<typeof formSchema>;

export function IdentitySubmitClient() {
  const nav = useNavigate();
  const { toast } = useToast();

  const identity = useMyIdentity();
  const presign = usePresignIdentity();
  const submit = useSubmitIdentity();
  const withdraw = useWithdrawIdentity();

  const [front, setFront] = React.useState<Pick>(null);
  const [back, setBack] = React.useState<Pick>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [uploadPct, setUploadPct] = React.useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onTouched",
    defaultValues: {
      idNumber: "",
      fullName: "",
      consent: false as unknown as true,
    },
  });

  if (identity.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  const current = identity.data;
  const status = current?.status ?? "NONE";
  const isPending = status === "PENDING";
  const isApproved = status === "APPROVED";
  const canSubmit = !isPending && !isApproved;
  const busy = presign.isPending || submit.isPending;

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    if (!front || !back) {
      setError("Vui lòng tải lên ảnh CCCD mặt trước và mặt sau.");
      return;
    }
    try {
      setUploadPct(0);
      // 1. presign → 2. upload both photos to R2 → 3. submit with the keys.
      const urls = await presign.mutateAsync({
        files: {
          idFront: {
            contentType: front.contentType,
            sizeBytes: front.file.size,
          },
          idBack: { contentType: back.contentType, sizeBytes: back.file.size },
        },
      });
      await putToSignedUrl(
        urls.idFront.uploadUrl,
        front.file,
        front.contentType,
      );
      setUploadPct(50);
      await putToSignedUrl(urls.idBack.uploadUrl, back.file, back.contentType);
      setUploadPct(100);
      await submit.mutateAsync({
        idNumber: values.idNumber,
        fullName: values.fullName,
        idFrontKey: urls.idFront.key,
        idBackKey: urls.idBack.key,
        consentAccepted: true,
      });
      toast({ tone: "success", title: "Đã gửi hồ sơ định danh" });
      nav("/profile");
    } catch (e) {
      const msg =
        e instanceof Error && e.message ? e.message : "Gửi hồ sơ thất bại";
      setError(msg);
      toast({ tone: "error", title: msg });
    }
  });

  return (
    <div className="space-y-6">
      <Link
        to="/profile"
        className="inline-flex items-center gap-1.5 text-body-sm font-semibold text-ink/70 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" />
        Quay lại
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-display-lg font-extrabold text-ink">
            Định danh cá nhân
          </h1>
          <p className="mt-1 max-w-xl text-body text-ash">
            Nộp CCCD một lần để xác minh danh tính. Dùng chung cho mọi doanh
            nghiệp bạn làm người đại diện.
          </p>
        </div>
        {status !== "NONE" ? <KycStatusBadge status={status} /> : null}
      </header>

      {status === "REJECTED" && current?.rejectCode ? (
        <div className="rounded-md border-l-4 border-danger bg-danger/5 p-4">
          <p className="text-body-sm font-bold uppercase text-danger">
            Hồ sơ bị từ chối
          </p>
          <p className="mt-1 text-body text-ink">
            {REJECT_HINTS[current.rejectCode] ?? "Vui lòng nộp lại hồ sơ."}
          </p>
          {current.rejectDetail ? (
            <p className="mt-1 text-body-sm text-ash">{current.rejectDetail}</p>
          ) : null}
        </div>
      ) : null}

      {isPending ? (
        <div className="rounded-md border-l-4 border-sun bg-sun/10 p-4 text-body text-ink">
          Hồ sơ đang chờ duyệt. Chúng tôi xử lý trong 1 ngày làm việc.
        </div>
      ) : null}

      {isApproved ? (
        <div className="rounded-md border-l-4 border-lime bg-lime/15 p-4 text-body text-ink">
          Danh tính của bạn đã được xác minh.
        </div>
      ) : null}

      {isPending ? (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            type="button"
            loading={withdraw.isPending}
            onClick={async () => {
              try {
                await withdraw.mutateAsync();
                toast({ tone: "success", title: "Đã rút hồ sơ" });
              } catch (e) {
                toast({
                  tone: "error",
                  title: e instanceof Error ? e.message : "Rút hồ sơ thất bại",
                });
              }
            }}
          >
            Rút hồ sơ để sửa
          </Button>
        </div>
      ) : null}

      <Card className="p-6">
        <div className="flex items-center gap-2">
          <IdCard size={18} className="text-ash" />
          <h2 className="text-title-lg font-bold text-ink">Thông tin CCCD</h2>
        </div>

        <form onSubmit={onSubmit} noValidate>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <FormField
              label="Họ và tên (như trên CCCD)"
              error={errors.fullName?.message}
            >
              <Input
                disabled={!canSubmit}
                placeholder="NGUYỄN VĂN A"
                invalid={!!errors.fullName}
                {...register("fullName")}
              />
            </FormField>
            <FormField label="Số CCCD" error={errors.idNumber?.message}>
              <Input
                disabled={!canSubmit}
                placeholder="012345678901"
                invalid={!!errors.idNumber}
                {...register("idNumber")}
              />
            </FormField>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <PhotoPickField
              label="Ảnh CCCD mặt trước"
              disabled={!canSubmit}
              value={front}
              onChange={setFront}
              onError={setError}
              required
            />
            <PhotoPickField
              label="Ảnh CCCD mặt sau"
              disabled={!canSubmit}
              value={back}
              onChange={setBack}
              onError={setError}
              required
            />
          </div>

          <label className="mt-6 flex cursor-pointer items-start gap-3 text-body-sm text-ink/80">
            <input
              type="checkbox"
              disabled={!canSubmit}
              className="mt-1 h-4 w-4 accent-lime"
              {...register("consent")}
            />
            <span>
              Tôi đồng ý cho Chốt Ca thu thập và xử lý CCCD để xác minh danh
              tính theo Chính sách bảo vệ dữ liệu cá nhân.
            </span>
          </label>
          {errors.consent ? (
            <p className="mt-1 text-body-sm text-danger">
              {errors.consent.message}
            </p>
          ) : null}

          {error ? (
            <p className="mt-4 rounded-md bg-danger/10 px-3 py-2 text-body-sm text-danger">
              {error}
            </p>
          ) : null}

          <div className="mt-6 flex justify-end">
            <Button type="submit" disabled={!canSubmit || busy} loading={busy}>
              {busy ? `Đang gửi ${uploadPct}%` : "Gửi hồ sơ"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function PhotoPickField({
  label,
  disabled,
  value,
  required,
  onChange,
  onError,
}: {
  label: string;
  disabled?: boolean;
  required?: boolean;
  value: Pick;
  onChange: (v: Pick) => void;
  onError: (msg: string) => void;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  function handlePick(file: File | undefined) {
    if (!file) return;
    if (!ALLOWED.has(file.type as "image/jpeg" | "image/png")) {
      onError("Chỉ chấp nhận ảnh JPG hoặc PNG.");
      return;
    }
    if (file.size > MAX_BYTES) {
      onError("Ảnh tối đa 10 MB.");
      return;
    }
    onChange({ file, contentType: file.type as "image/jpeg" | "image/png" });
  }

  const previewUrl = React.useMemo(
    () => (value ? URL.createObjectURL(value.file) : null),
    [value],
  );
  React.useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2 text-body-sm font-semibold text-ink">
        <span>{label}</span>
        {required ? <span className="text-danger">*</span> : null}
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        className={[
          "group flex w-full flex-col items-center justify-center overflow-hidden rounded-md border-2 border-dashed px-4 py-6 text-center transition",
          disabled
            ? "cursor-not-allowed border-smoke bg-mist/40 opacity-60"
            : value
              ? "border-lime bg-lime/5 hover:bg-lime/10"
              : "border-smoke bg-paper hover:border-ink/40 hover:bg-mist",
        ].join(" ")}
      >
        {value && previewUrl ? (
          <img
            src={previewUrl}
            alt={label}
            className="max-h-40 w-auto rounded object-contain"
          />
        ) : (
          <>
            <UploadCloud className="h-7 w-7 text-ink/50" />
            <p className="mt-2 text-body-sm font-semibold text-ink">Chọn ảnh</p>
            <p className="mt-0.5 text-xs text-ash">
              JPG hoặc PNG, tối đa 10 MB
            </p>
          </>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept="image/jpeg,image/png"
        disabled={disabled}
        onChange={(e) => {
          handlePick(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
    </div>
  );
}
